<script setup>
// Ozel kitleler sekmesi: listeleme, yeni kitle, benzer (lookalike) kitle,
// paylasim ve silme.
//
// Silme ve paylasim geri alinamaz ya da baska hesaplari etkiler; bu yuzden
// once window.confirm, sonra { onaylandi: true } ile arac cagrilir. Sunucu
// onaysiz yazma isteklerini 428 ile geri cevirir.
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import { aracCalistir, diziyeCevir, sayiBicimle } from './kopru.js';
import AracTablosu from './AracTablosu.vue';

const props = defineProps({
  hesap: { type: String, default: '' },
  hesapAyrinti: { type: Object, default: () => ({}) },
});

const { t } = useI18n();
const route = useRoute();

const satirlar = ref([]);
const yukleniyor = ref(false);
const hata = ref('');

// Tek seferde tek panel acilir; yan yana form gostermek dar ekranda okunmaz.
const panel = ref('');
const islemde = ref(false);

const yeniForm = ref({ ad: '', aciklama: '', altTur: 'CUSTOM' });
const benzerForm = ref({ ad: '', kaynak: '', ulke: 'TR', oran: '0.01' });

const ayrinti = ref(null);
const paylasim = ref({ kitle: null, hesaplar: [], hedefHesap: '' });

const accountId = computed(() => route.params.accountId);

// Meta bazi surumlerde alt/ust sinir, bazilarinda tek sayi dondurur.
const boyutMetni = satir => {
  const alt = satir?.approximate_count_lower_bound;
  const ust = satir?.approximate_count_upper_bound;
  if (alt != null && ust != null) return `${sayiBicimle(alt)} - ${sayiBicimle(ust)}`;
  const tek = satir?.approximate_count ?? alt ?? ust;
  return tek == null ? '—' : sayiBicimle(tek);
};

// time_updated kimi yanitlarda unix saniye, kimilerinde ISO metin gelir.
const tarihMetni = deger => {
  if (!deger) return '—';
  const zaman = /^\d+$/.test(String(deger)) ? Number(deger) * 1000 : Date.parse(deger);
  if (Number.isNaN(zaman)) return String(deger);
  return new Intl.DateTimeFormat('tr-TR', { dateStyle: 'short', timeStyle: 'short' }).format(zaman);
};

// --- Ayrinti panelinin okunur gosterimi ---------------------------------
// Meta'nin alan adlari (subtype, time_updated...) kullaniciya bir sey
// anlatmadigi icin anahtar -> ceviri eslemesi tutuluyor. Listede olmayan
// anahtar oldugu gibi basilir; uydurma etiket kullaniciyi yaniltir.
const ALAN_ETIKET = {
  id: 'KIMLIK',
  account_id: 'HESAP',
  name: 'AD',
  description: 'ACIKLAMA',
  subtype: 'TUR',
  approximate_count: 'BOYUT',
  approximate_count_lower_bound: 'BOYUT_ALT',
  approximate_count_upper_bound: 'BOYUT_UST',
  time_created: 'OLUSTURMA',
  time_updated: 'GUNCELLEME',
  time_content_updated: 'ICERIK_GUNCELLEME',
  delivery_status: 'DURUM',
  operation_status: 'ISLEM_DURUMU',
  retention_days: 'SAKLAMA',
  rule: 'KURAL',
  rule_aggregation: 'KURAL_TOPLAMA',
  data_source: 'VERI_KAYNAGI',
  customer_file_source: 'MUSTERI_KAYNAGI',
  permission_for_actions: 'IZIN',
  is_value_based: 'DEGER_TABANLI',
  lookalike_spec: 'BENZER_AYAR',
  lookalike_audience_ids: 'BENZER_KITLELER',
  sharing_status: 'PAYLASIM_DURUMU',
  origin_audience_id: 'KAYNAK_KITLE',
  ratio: 'ORAN',
  country: 'ULKE',
  pixel_id: 'PIKSEL',
  external_event_source: 'OLAY_KAYNAGI',
  opt_out_link: 'CIKIS_BAGLANTISI',
};

// Tarih ve sayi alanlari Meta'dan duz sayi olarak gelir; turlerine bakarak
// ayirt edilemezler, bu yuzden ad bazinda tanimlaniyorlar.
const TARIH_ALANLARI = ['time_created', 'time_updated', 'time_content_updated'];
const SAYI_ALANLARI = [
  'approximate_count',
  'approximate_count_lower_bound',
  'approximate_count_upper_bound',
  'retention_days',
];

const etiketle = anahtar => {
  const sonEk = ALAN_ETIKET[anahtar];
  return sonEk ? t(`META_ADS.HEDEF_KITLELER.KITLELER.AYRINTI.ALAN.${sonEk}`) : anahtar;
};

// Ic ice nesne ve diziler ham JSON'a donusmesin diye tek satirda ozetlenir.
const degerMetni = (anahtar, deger) => {
  if (deger === null || deger === undefined || deger === '') return '';
  if (TARIH_ALANLARI.includes(anahtar)) return tarihMetni(deger);
  if (typeof deger === 'boolean') {
    return deger
      ? t('META_ADS.HEDEF_KITLELER.KITLELER.AYRINTI.EVET')
      : t('META_ADS.HEDEF_KITLELER.KITLELER.AYRINTI.HAYIR');
  }
  if (SAYI_ALANLARI.includes(anahtar)) return sayiBicimle(deger);
  if (Array.isArray(deger)) {
    if (!deger.length) return '';
    // Kisa ve duz listeler tam yazilir; uzun ya da nesne iceren listeler sayilir.
    const duz = deger.every(oge => oge === null || typeof oge !== 'object');
    if (duz && deger.length <= 5) return deger.join(', ');
    return t('META_ADS.HEDEF_KITLELER.KITLELER.AYRINTI.OGE', { sayi: deger.length });
  }
  if (typeof deger === 'object') {
    // Meta durum alanlari { code, description } bicimindedir.
    if (deger.description || deger.code) return String(deger.description || deger.code);
    const girdiler = Object.entries(deger).filter(
      ([, d]) => d !== null && d !== '' && typeof d !== 'object'
    );
    if (girdiler.length && girdiler.length <= 4) {
      return girdiler.map(([a, d]) => `${etiketle(a)}: ${d}`).join(' · ');
    }
    return t('META_ADS.HEDEF_KITLELER.KITLELER.AYRINTI.ALAN_SAYISI', {
      sayi: Object.keys(deger).length,
    });
  }
  return String(deger);
};

// Bos alanlar listeye girmez; "—" dolu bir ekran bilgi vermez.
const ayrintiAlanlari = computed(() => {
  const veri = ayrinti.value?.veri;
  const kok = Array.isArray(veri) ? veri[0] : veri;
  if (!kok || typeof kok !== 'object') return [];
  return Object.entries(kok)
    .map(([anahtar, deger]) => ({
      anahtar,
      etiket: etiketle(anahtar),
      deger: degerMetni(anahtar, deger),
    }))
    .filter(alan => alan.deger !== '');
});

const sutunlar = computed(() => [
  { anahtar: 'name', baslik: t('META_ADS.HEDEF_KITLELER.KITLELER.SUTUN.AD') },
  { anahtar: 'subtype', baslik: t('META_ADS.HEDEF_KITLELER.KITLELER.SUTUN.TUR') },
  {
    anahtar: 'approximate_count_lower_bound',
    baslik: t('META_ADS.HEDEF_KITLELER.KITLELER.SUTUN.BOYUT'),
    bicim: (_deger, satir) => boyutMetni(satir),
  },
  {
    anahtar: 'delivery_status',
    baslik: t('META_ADS.HEDEF_KITLELER.KITLELER.SUTUN.DURUM'),
    // delivery_status kimi yanitta { code, description } nesnesi, kiminde duz
    // metindir; nesneyi oldugu gibi dondurursek hucrede [object Object] yazar.
    bicim: deger => {
      if (!deger) return '—';
      if (typeof deger === 'object') return deger.description || deger.code || '—';
      return String(deger);
    },
  },
  {
    anahtar: 'time_updated',
    baslik: t('META_ADS.HEDEF_KITLELER.KITLELER.SUTUN.GUNCELLEME'),
    bicim: tarihMetni,
  },
]);

const yukle = async () => {
  if (!props.hesap) return;
  yukleniyor.value = true;
  hata.value = '';
  try {
    const sonuc = await aracCalistir(accountId.value, 'ads_get_custom_audiences', {
      account_id: props.hesap,
    });
    satirlar.value = diziyeCevir(sonuc);
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
    satirlar.value = [];
  } finally {
    yukleniyor.value = false;
  }
};

const panelAc = (ad, kitle = null) => {
  hata.value = '';
  if (ad === 'benzer') {
    benzerForm.value = {
      // Onerilen ad kullaniciya gorunur, o yuzden ceviriden gelir.
      ad: kitle
        ? t('META_ADS.HEDEF_KITLELER.KITLELER.FORM.BENZER_AD_ONERI', {
            ad: kitle.name,
          })
        : '',
      kaynak: kitle?.id || '',
      ulke: 'TR',
      oran: '0.01',
    };
  }
  panel.value = panel.value === ad && !kitle ? '' : ad;
};

const panelKapat = () => {
  panel.value = '';
  ayrinti.value = null;
};

const yeniKitleOlustur = async () => {
  if (!yeniForm.value.ad.trim()) {
    hata.value = t('META_ADS.HEDEF_KITLELER.KITLELER.FORM.AD_GEREKLI');
    return;
  }
  // Olusturulan kitle Meta hesabinda kalici kayit acar ve ayni adla ikinci kez
  // olusturulursa kullanici hangisinin dogru oldugunu ayirt edemez; bu yuzden
  // diger yazma islemleri gibi once onay isteniyor.
  if (!window.confirm(t('META_ADS.HEDEF_KITLELER.KITLELER.FORM.OLUSTUR_ONAY'))) return;
  islemde.value = true;
  hata.value = '';
  try {
    await aracCalistir(
      accountId.value,
      'ads_create_custom_audience',
      {
        account_id: props.hesap,
        name: yeniForm.value.ad.trim(),
        description: yeniForm.value.aciklama.trim(),
        subtype: yeniForm.value.altTur,
      },
      { onaylandi: true }
    );
    useAlert(t('META_ADS.HEDEF_KITLELER.KITLELER.OLUSTURULDU'));
    yeniForm.value = { ad: '', aciklama: '', altTur: 'CUSTOM' };
    panelKapat();
    await yukle();
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    islemde.value = false;
  }
};

const benzerKitleOlustur = async () => {
  if (!benzerForm.value.ad.trim() || !benzerForm.value.kaynak) {
    hata.value = t('META_ADS.HEDEF_KITLELER.KITLELER.FORM.AD_GEREKLI');
    return;
  }
  // Benzer kitle Meta'da hesaplanmasi saatler suren kalici bir kayit; yanlis
  // kaynak ya da oranla acilirsa silinip yeniden kurulmasi gerekir.
  if (!window.confirm(t('META_ADS.HEDEF_KITLELER.KITLELER.FORM.BENZER_ONAY'))) return;
  islemde.value = true;
  hata.value = '';
  try {
    await aracCalistir(
      accountId.value,
      'ads_create_lookalike_audience',
      {
        account_id: props.hesap,
        name: benzerForm.value.ad.trim(),
        // Meta'nin lookalike spec alanlari: kaynak kitle, ulke ve orandir.
        origin_audience_id: benzerForm.value.kaynak,
        country: benzerForm.value.ulke,
        ratio: Number(benzerForm.value.oran),
      },
      { onaylandi: true }
    );
    useAlert(t('META_ADS.HEDEF_KITLELER.KITLELER.BENZER_OLUSTURULDU'));
    panelKapat();
    await yukle();
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    islemde.value = false;
  }
};

const ayrintiAc = async kitle => {
  panel.value = 'ayrinti';
  ayrinti.value = null;
  islemde.value = true;
  hata.value = '';
  try {
    const sonuc = await aracCalistir(accountId.value, 'ads_get_audience_details', {
      account_id: props.hesap,
      audience_id: kitle.id,
    });
    ayrinti.value = { kitle, veri: sonuc.veri, ozet: sonuc.ozet };
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    islemde.value = false;
  }
};

const paylasimAc = async kitle => {
  panel.value = 'paylasim';
  paylasim.value = { kitle, hesaplar: [], hedefHesap: '' };
  islemde.value = true;
  hata.value = '';
  try {
    const sonuc = await aracCalistir(accountId.value, 'ads_get_audience_shared_accounts', {
      account_id: props.hesap,
      audience_id: kitle.id,
    });
    paylasim.value.hesaplar = diziyeCevir(sonuc);
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    islemde.value = false;
  }
};

// Paylasim baska bir reklam hesabina erisim verir; onay olmadan calismaz.
const paylasimDegistir = async kaldir => {
  const hedef = kaldir || paylasim.value.hedefHesap.trim();
  if (!hedef || !paylasim.value.kitle) return;
  const soru = kaldir
    ? t('META_ADS.HEDEF_KITLELER.KITLELER.PAYLASIM.KALDIR_ONAY')
    : t('META_ADS.HEDEF_KITLELER.KITLELER.PAYLASIM.PAYLAS_ONAY');
  if (!window.confirm(`${hedef} ${soru}`)) return;
  islemde.value = true;
  hata.value = '';
  try {
    await aracCalistir(
      accountId.value,
      kaldir ? 'ads_unshare_custom_audience' : 'ads_share_custom_audience',
      {
        account_id: props.hesap,
        audience_id: paylasim.value.kitle.id,
        target_account_ids: [hedef],
      },
      { onaylandi: true }
    );
    useAlert(
      kaldir
        ? t('META_ADS.HEDEF_KITLELER.KITLELER.PAYLASIM_KALDIRILDI')
        : t('META_ADS.HEDEF_KITLELER.KITLELER.PAYLASILDI')
    );
    await paylasimAc(paylasim.value.kitle);
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    islemde.value = false;
  }
};

const sil = async kitle => {
  // Silinen kitle geri gelmez; reklam setleri de hedefsiz kalabilir.
  if (!window.confirm(`${kitle.name} ${t('META_ADS.HEDEF_KITLELER.KITLELER.SIL_ONAY')}`)) return;
  islemde.value = true;
  hata.value = '';
  try {
    await aracCalistir(
      accountId.value,
      'ads_delete_custom_audience',
      { account_id: props.hesap, audience_id: kitle.id },
      { onaylandi: true }
    );
    useAlert(t('META_ADS.HEDEF_KITLELER.KITLELER.SILINDI'));
    panelKapat();
    await yukle();
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    islemde.value = false;
  }
};

watch(() => props.hesap, yukle);
onMounted(yukle);
</script>

<template>
  <div class="p-6">
    <div class="flex flex-wrap items-center gap-2 mb-4">
      <Button
        variant="faded"
        size="sm"
        icon="i-lucide-refresh-cw"
        :label="t('META_ADS.HEDEF_KITLELER.KITLELER.YENILE')"
        :disabled="!hesap || yukleniyor"
        @click="yukle"
      />
      <Button
        variant="faded"
        size="sm"
        icon="i-lucide-plus"
        :label="t('META_ADS.HEDEF_KITLELER.KITLELER.YENI_KITLE')"
        :disabled="!hesap"
        @click="panelAc('yeni')"
      />
      <Button
        variant="faded"
        size="sm"
        icon="i-lucide-users"
        :label="t('META_ADS.HEDEF_KITLELER.KITLELER.BENZER_KITLE')"
        :disabled="!hesap"
        @click="panelAc('benzer')"
      />
    </div>

    <p v-if="!hesap" class="text-sm text-n-slate-11">
      {{ t('META_ADS.HEDEF_KITLELER.KITLELER.HESAP_SEC') }}
    </p>
    <p v-if="hata" class="mb-3 text-sm text-n-ruby-11">{{ hata }}</p>

    <!-- Yeni ozel kitle -->
    <div v-if="panel === 'yeni'" class="p-4 mb-4 border rounded-lg border-n-weak">
      <h3 class="mb-3 text-sm font-medium text-n-slate-12">
        {{ t('META_ADS.HEDEF_KITLELER.KITLELER.YENI_KITLE') }}
      </h3>
      <div class="grid gap-3 md:grid-cols-3">
        <label class="text-sm text-n-slate-11">
          {{ t('META_ADS.HEDEF_KITLELER.KITLELER.FORM.AD') }}
          <input v-model="yeniForm.ad" type="text" />
        </label>
        <label class="text-sm text-n-slate-11">
          {{ t('META_ADS.HEDEF_KITLELER.KITLELER.FORM.ACIKLAMA') }}
          <input v-model="yeniForm.aciklama" type="text" />
        </label>
        <label class="text-sm text-n-slate-11">
          {{ t('META_ADS.HEDEF_KITLELER.KITLELER.FORM.ALT_TUR') }}
          <select v-model="yeniForm.altTur">
            <option value="CUSTOM">CUSTOM</option>
            <option value="WEBSITE">WEBSITE</option>
            <option value="ENGAGEMENT">ENGAGEMENT</option>
          </select>
        </label>
      </div>
      <div class="flex gap-2 mt-2">
        <Button
          size="sm"
          :label="t('META_ADS.HEDEF_KITLELER.KITLELER.FORM.OLUSTUR')"
          :disabled="islemde"
          @click="yeniKitleOlustur"
        />
        <Button
          variant="faded"
          size="sm"
          :label="t('META_ADS.HEDEF_KITLELER.KITLELER.FORM.VAZGEC')"
          @click="panelKapat"
        />
      </div>
    </div>

    <!-- Benzer (lookalike) kitle -->
    <div v-if="panel === 'benzer'" class="p-4 mb-4 border rounded-lg border-n-weak">
      <h3 class="mb-3 text-sm font-medium text-n-slate-12">
        {{ t('META_ADS.HEDEF_KITLELER.KITLELER.BENZER_KITLE') }}
      </h3>
      <div class="grid gap-3 md:grid-cols-4">
        <label class="text-sm text-n-slate-11">
          {{ t('META_ADS.HEDEF_KITLELER.KITLELER.FORM.AD') }}
          <input v-model="benzerForm.ad" type="text" />
        </label>
        <label class="text-sm text-n-slate-11">
          {{ t('META_ADS.HEDEF_KITLELER.KITLELER.FORM.KAYNAK_KITLE') }}
          <select v-model="benzerForm.kaynak">
            <option value="">—</option>
            <option v-for="k in satirlar" :key="k.id" :value="k.id">{{ k.name }}</option>
          </select>
        </label>
        <label class="text-sm text-n-slate-11">
          {{ t('META_ADS.HEDEF_KITLELER.KITLELER.FORM.ULKE') }}
          <input v-model="benzerForm.ulke" type="text" maxlength="2" />
        </label>
        <label class="text-sm text-n-slate-11">
          {{ t('META_ADS.HEDEF_KITLELER.KITLELER.FORM.ORAN') }}
          <select v-model="benzerForm.oran">
            <option value="0.01">1%</option>
            <option value="0.02">2%</option>
            <option value="0.05">5%</option>
            <option value="0.1">10%</option>
          </select>
        </label>
      </div>
      <div class="flex gap-2 mt-2">
        <Button
          size="sm"
          :label="t('META_ADS.HEDEF_KITLELER.KITLELER.FORM.OLUSTUR')"
          :disabled="islemde"
          @click="benzerKitleOlustur"
        />
        <Button
          variant="faded"
          size="sm"
          :label="t('META_ADS.HEDEF_KITLELER.KITLELER.FORM.VAZGEC')"
          @click="panelKapat"
        />
      </div>
    </div>

    <!-- Kitle ayrintisi -->
    <div v-if="panel === 'ayrinti'" class="p-4 mb-4 border rounded-lg border-n-weak">
      <div class="flex items-center justify-between mb-2">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('META_ADS.HEDEF_KITLELER.KITLELER.AYRINTI.BASLIK') }}
          <span v-if="ayrinti" class="text-n-slate-11">· {{ ayrinti.kitle.name }}</span>
        </h3>
        <Button
          variant="faded"
          size="sm"
          :label="t('META_ADS.HEDEF_KITLELER.KITLELER.AYRINTI.KAPAT')"
          @click="panelKapat"
        />
      </div>
      <!-- Ham JSON yerine alan/deger listesi: kullanici hangi bilginin ne
           oldugunu ancak etiketle anlar. -->
      <p v-if="islemde && !ayrinti" class="text-sm text-n-slate-11">
        {{ t('META_ADS.HEDEF_KITLELER.KITLELER.AYRINTI.YUKLENIYOR') }}
      </p>
      <template v-else-if="ayrinti">
        <p v-if="ayrinti.ozet" class="mb-3 text-sm whitespace-pre-line text-n-slate-11">
          {{ ayrinti.ozet }}
        </p>
        <dl
          v-if="ayrintiAlanlari.length"
          class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 overflow-auto text-sm max-h-72"
        >
          <template v-for="alan in ayrintiAlanlari" :key="alan.anahtar">
            <dt class="text-n-slate-11">{{ alan.etiket }}</dt>
            <dd class="break-words text-n-slate-12">{{ alan.deger }}</dd>
          </template>
        </dl>
        <p v-else-if="!ayrinti.ozet" class="text-sm text-n-slate-11">
          {{ t('META_ADS.HEDEF_KITLELER.KITLELER.AYRINTI.BOS') }}
        </p>
      </template>
    </div>

    <!-- Paylasim -->
    <div v-if="panel === 'paylasim'" class="p-4 mb-4 border rounded-lg border-n-weak">
      <div class="flex items-center justify-between mb-2">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('META_ADS.HEDEF_KITLELER.KITLELER.PAYLASIM.BASLIK') }}
          <span v-if="paylasim.kitle" class="text-n-slate-11">· {{ paylasim.kitle.name }}</span>
        </h3>
        <Button
          variant="faded"
          size="sm"
          :label="t('META_ADS.HEDEF_KITLELER.KITLELER.AYRINTI.KAPAT')"
          @click="panelKapat"
        />
      </div>

      <ul v-if="paylasim.hesaplar.length" class="mb-3 text-sm">
        <li
          v-for="h in paylasim.hesaplar"
          :key="h.id || h.account_id"
          class="flex items-center gap-3 py-1 text-n-slate-12"
        >
          <span>{{ h.name || h.account_id || h.id }}</span>
          <Button
            variant="faded"
            size="sm"
            :label="t('META_ADS.HEDEF_KITLELER.KITLELER.PAYLASIM.KALDIR')"
            :disabled="islemde"
            @click="paylasimDegistir(h.account_id || h.id)"
          />
        </li>
      </ul>
      <p v-else class="mb-3 text-sm text-n-slate-11">
        {{ t('META_ADS.HEDEF_KITLELER.KITLELER.PAYLASIM.BOS') }}
      </p>

      <div class="flex flex-wrap items-end gap-2">
        <label class="text-sm text-n-slate-11">
          {{ t('META_ADS.HEDEF_KITLELER.KITLELER.PAYLASIM.HESAP_ID') }}
          <input v-model="paylasim.hedefHesap" type="text" placeholder="act_123456789" />
        </label>
        <Button
          size="sm"
          :label="t('META_ADS.HEDEF_KITLELER.KITLELER.PAYLASIM.PAYLAS')"
          :disabled="islemde || !paylasim.hedefHesap"
          @click="paylasimDegistir('')"
        />
      </div>
    </div>

    <AracTablosu
      :satirlar="satirlar"
      :sutunlar="sutunlar"
      :yukleniyor="yukleniyor"
      :bos-metin="t('META_ADS.HEDEF_KITLELER.KITLELER.BOS')"
    >
      <template #eylemler="{ satir }">
        <Button
          variant="faded"
          size="sm"
          icon="i-lucide-info"
          :label="t('META_ADS.HEDEF_KITLELER.KITLELER.EYLEM.AYRINTI')"
          @click="ayrintiAc(satir)"
        />
        <Button
          variant="faded"
          size="sm"
          icon="i-lucide-users"
          :label="t('META_ADS.HEDEF_KITLELER.KITLELER.EYLEM.BENZER')"
          @click="panelAc('benzer', satir)"
        />
        <Button
          variant="faded"
          size="sm"
          icon="i-lucide-share-2"
          :label="t('META_ADS.HEDEF_KITLELER.KITLELER.EYLEM.PAYLAS')"
          @click="paylasimAc(satir)"
        />
        <Button
          variant="faded"
          size="sm"
          icon="i-lucide-trash-2"
          :label="t('META_ADS.HEDEF_KITLELER.KITLELER.EYLEM.SIL')"
          :disabled="islemde"
          @click="sil(satir)"
        />
      </template>
    </AracTablosu>
  </div>
</template>
